import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
    Box,
    Typography,
    TextField,
    Button,
    Paper,
    LinearProgress,
    List,
    ListItemButton,
    ListItemText,
    Chip,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { authHeader } from "../auth";

// Poste "cabane" — file des 3 prochains dossards (comme les postes
// distribués), mais chaque carte réunit les 4 valeurs d'un coup :
// Style Juge 1 | Style Juge 2 | Total (J1+J2, calculé) | Jump — au lieu
// d'être éclatées sur des pages séparées. Un seul bouton "Confirmer" par
// carte enregistre les 3 champs modifiables en une fois.
//
// URL : /saisiepenalite/:eventKey/solo
//
// Écrit sur EXACTEMENT les mêmes emplacements que les postes distribués
// (style/slot1, style/slot2, jump/slot1) — les données sont donc
// synchronisées entre cette page et les 6 liens du hub, quel que soit le
// mode utilisé pour saisir un dossard donné.
//
// AUCUNE activation automatique de case (pas de focus, pas de minuteur) —
// on touche la carte qu'on veut, quand on veut.

const POLL_MS = 5000;
const VISIBLE_COUNT = 3;

function athleteName(a) {
    return `${a.lastname || ""} ${a.firstname || ""}`.trim();
}

function toNumber(str) {
    const n = Number(String(str ?? "").replace(",", "."));
    return Number.isFinite(n) ? n : 0;
}

const EMPTY_ENTRY = { style1: "", style2: "", jump: "" };

export default function SaisiePenaliteSolo() {
    const { eventKey } = useParams();

    const [startlist, setStartlist] = useState([]);
    const [entries, setEntries] = useState([]);
    const [error, setError] = useState("");
    const [info, setInfo] = useState("");
    const [selectedBib, setSelectedBib] = useState(null);
    const [savingBib, setSavingBib] = useState(null);

    const [values, setValues] = useState({}); // bib -> { style1, style2, jump }
    const [correctionValue, setCorrectionValue] = useState(EMPTY_ENTRY);

    const apiFetch = useCallback(
        (url, options = {}) =>
            fetch(url, {
                ...options,
                headers: { "Content-Type": "application/json", ...authHeader(), ...(options.headers || {}) },
            }),
        []
    );

    const loadStartlist = useCallback(async () => {
        try {
            const res = await apiFetch(`/api/timing/startlist/${encodeURIComponent(eventKey)}`);
            if (res.ok) setStartlist(await res.json());
        } catch (err) {
            setError(err.message);
        }
    }, [apiFetch, eventKey]);

    const loadEntries = useCallback(async () => {
        try {
            const res = await apiFetch(`/api/timing/penalty-entries/${encodeURIComponent(eventKey)}`);
            if (res.ok) setEntries(await res.json());
        } catch (err) {
            setError(err.message);
        }
    }, [apiFetch, eventKey]);

    useEffect(() => {
        loadStartlist();
        loadEntries();
        const t = setInterval(() => {
            loadStartlist();
            loadEntries();
        }, POLL_MS);
        return () => clearInterval(t);
    }, [loadStartlist, loadEntries]);

    // Regroupe les 3 entrées possibles par dossard — quelle que soit leur
    // origine (cette page ou un poste distribué), même table côté serveur.
    const entriesByBib = useMemo(() => {
        const map = {};
        entries.forEach((e) => {
            const bib = String(e.bib);
            if (!map[bib]) map[bib] = {};
            if (e.category === "style" && Number(e.slot) === 1) map[bib].style1 = e;
            if (e.category === "style" && Number(e.slot) === 2) map[bib].style2 = e;
            if (e.category === "jump" && Number(e.slot) === 1) map[bib].jump = e;
        });
        return map;
    }, [entries]);

    // "Confirmé" = le champ Jump est présent (dernier champ de la carte,
    // toujours rempli quel que soit le nombre de juges style disponibles).
    const isDone = (bib) => !!entriesByBib[String(bib)]?.jump;

    const pending = useMemo(() => startlist.filter((a) => !isDone(a.bib)), [startlist, entriesByBib]);
    const done = useMemo(() => startlist.filter((a) => isDone(a.bib)), [startlist, entriesByBib]);

    const visiblePending = pending.slice(0, VISIBLE_COUNT);
    const visibleBibsKey = visiblePending.map((a) => String(a.bib)).join(",");

    useEffect(() => {
        setValues((prev) => {
            let changed = false;
            const next = { ...prev };
            visiblePending.forEach((a) => {
                const bib = String(a.bib);
                if (!(bib in next)) {
                    next[bib] = { ...EMPTY_ENTRY };
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visibleBibsKey]);

    const setValueField = (bib, field, val) => {
        setValues((prev) => ({ ...prev, [bib]: { ...(prev[bib] || EMPTY_ENTRY), [field]: val } }));
    };

    const savePenalty = async (bib, v) => {
        setSavingBib(bib);
        setError("");
        setInfo("");
        try {
            const [r1, r2, r3] = await Promise.all([
                apiFetch(`/api/timing/penalty-entries/${encodeURIComponent(eventKey)}/${encodeURIComponent(bib)}/style/1`, {
                    method: "POST",
                    body: JSON.stringify({ value: toNumber(v.style1) }),
                }),
                apiFetch(`/api/timing/penalty-entries/${encodeURIComponent(eventKey)}/${encodeURIComponent(bib)}/style/2`, {
                    method: "POST",
                    body: JSON.stringify({ value: toNumber(v.style2) }),
                }),
                apiFetch(`/api/timing/penalty-entries/${encodeURIComponent(eventKey)}/${encodeURIComponent(bib)}/jump/1`, {
                    method: "POST",
                    body: JSON.stringify({ value: toNumber(v.jump) }),
                }),
            ]);

            const bad = [r1, r2, r3].find((r) => !r.ok);
            if (bad) {
                const data = await bad.json().catch(() => ({}));
                setError(data.error || `HTTP ${bad.status}`);
                return false;
            }

            const now = new Date().toISOString();
            setEntries((prev) => [
                ...prev.filter(
                    (e) =>
                        !(
                            String(e.bib) === bib &&
                            ((e.category === "style" && (Number(e.slot) === 1 || Number(e.slot) === 2)) ||
                                e.category === "jump")
                        )
                ),
                { bib, category: "style", slot: 1, value: toNumber(v.style1), updatedAt: now },
                { bib, category: "style", slot: 2, value: toNumber(v.style2), updatedAt: now },
                { bib, category: "jump", slot: 1, value: toNumber(v.jump), updatedAt: now },
            ]);
            setInfo(`Dossard ${bib} confirmé ✓`);
            return true;
        } catch (err) {
            setError(err.message);
            return false;
        } finally {
            setSavingBib(null);
        }
    };

    const confirmPending = async (bib) => {
        const v = values[bib] || EMPTY_ENTRY;
        const ok = await savePenalty(bib, v);
        if (ok) {
            setValues((prev) => {
                const next = { ...prev };
                delete next[bib];
                return next;
            });
        }
    };

    const openCorrection = (bib) => {
        const e = entriesByBib[String(bib)] || {};
        setCorrectionValue({
            style1: e.style1 ? String(e.style1.value) : "",
            style2: e.style2 ? String(e.style2.value) : "",
            jump: e.jump ? String(e.jump.value) : "",
        });
        setSelectedBib(bib);
    };

    const confirmCorrection = async () => {
        if (!selectedBib) return;
        const ok = await savePenalty(String(selectedBib), correctionValue);
        if (ok) setSelectedBib(null);
    };

    const total = startlist.length;
    const doneCount = done.length;
    const correctionAthlete = selectedBib ? startlist.find((a) => String(a.bib) === String(selectedBib)) : null;

    // Les 4 champs d'une carte, dans l'ordre demandé : J1, J2, Total
    // (calculé), Jump.
    const renderFields = (bib, entry, onChange) => {
        const totalStyle = toNumber(entry.style1) + toNumber(entry.style2);
        return (
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
                <TextField
                    label="Juge 1 (s)"
                    type="tel"
                    inputMode="decimal"
                    value={entry.style1}
                    onChange={(e) => onChange("style1", e.target.value)}
                    sx={{ flex: "1 1 70px", "& input": { fontSize: 20, textAlign: "center", py: 1.1 } }}
                />
                <TextField
                    label="Juge 2 (s)"
                    type="tel"
                    inputMode="decimal"
                    value={entry.style2}
                    onChange={(e) => onChange("style2", e.target.value)}
                    sx={{ flex: "1 1 70px", "& input": { fontSize: 20, textAlign: "center", py: 1.1 } }}
                />
                <TextField
                    label="Total J1+J2"
                    value={totalStyle.toFixed(2)}
                    InputProps={{ readOnly: true }}
                    sx={{
                        flex: "1 1 80px",
                        "& input": { fontSize: 20, textAlign: "center", py: 1.1, fontWeight: "bold" },
                        bgcolor: "#f0f0f0",
                    }}
                />
                <TextField
                    label="Jump (s)"
                    type="tel"
                    inputMode="decimal"
                    value={entry.jump}
                    onChange={(e) => onChange("jump", e.target.value)}
                    sx={{ flex: "1 1 70px", "& input": { fontSize: 20, textAlign: "center", py: 1.1 } }}
                />
            </Box>
        );
    };

    return (
        <Box sx={{ minHeight: "100vh", bgcolor: "#f5f5f5", p: 2, pb: 6 }}>
            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 0.25 }}>
                Saisie unique (cabane) — {eventKey}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Juge 1, Juge 2, Total calculé, Jump
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {doneCount} / {total} confirmés
            </Typography>
            <LinearProgress
                variant="determinate"
                value={total ? (doneCount / total) * 100 : 0}
                sx={{ mb: 2, height: 8, borderRadius: 4 }}
            />

            {error && (
                <Typography color="error" variant="body2" sx={{ mb: 1 }}>
                    {error}
                </Typography>
            )}
            {info && !error && (
                <Typography color="success.main" variant="body2" sx={{ mb: 1 }}>
                    {info}
                </Typography>
            )}

            {selectedBib && correctionAthlete && (
                <Paper sx={{ p: 2.5, mb: 3, borderRadius: 3 }} elevation={3}>
                    <Chip label="Correction" size="small" color="warning" sx={{ mb: 1 }} />
                    <Typography variant="h5" sx={{ fontWeight: "bold", textAlign: "center", mb: 0.5 }}>
                        [{correctionAthlete.bib}]
                    </Typography>
                    <Typography variant="subtitle1" sx={{ textAlign: "center", mb: 2 }}>
                        {athleteName(correctionAthlete) || "—"}
                    </Typography>

                    {renderFields(String(selectedBib), correctionValue, (field, val) =>
                        setCorrectionValue((prev) => ({ ...prev, [field]: val }))
                    )}

                    <Button
                        variant="contained"
                        color="success"
                        size="large"
                        fullWidth
                        onClick={confirmCorrection}
                        disabled={savingBib === String(selectedBib)}
                        sx={{ py: 1.5, fontWeight: "bold" }}
                    >
                        {savingBib === String(selectedBib) ? "Enregistrement…" : "Enregistrer la correction ✓"}
                    </Button>
                    <Button fullWidth sx={{ mt: 1 }} onClick={() => setSelectedBib(null)}>
                        Annuler
                    </Button>
                </Paper>
            )}

            {!selectedBib && visiblePending.length > 0 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 3 }}>
                    {visiblePending.map((a, idx) => {
                        const bib = String(a.bib);
                        const entry = values[bib] || EMPTY_ENTRY;
                        return (
                            <Paper key={bib} sx={{ p: 2, borderRadius: 3 }} elevation={idx === 0 ? 3 : 1}>
                                <Typography variant="h5" sx={{ fontWeight: "bold", textAlign: "center", mb: 0.25 }}>
                                    [{a.bib}]
                                </Typography>
                                <Typography variant="subtitle1" sx={{ textAlign: "center", mb: 1.5 }}>
                                    {athleteName(a) || "—"}
                                </Typography>

                                {renderFields(bib, entry, (field, val) => setValueField(bib, field, val))}

                                <Button
                                    variant="contained"
                                    color="success"
                                    fullWidth
                                    onClick={() => confirmPending(bib)}
                                    disabled={savingBib === bib}
                                    sx={{ py: 1.3, fontWeight: "bold" }}
                                >
                                    {savingBib === bib ? "Enregistrement…" : "Confirmer ✓"}
                                </Button>
                            </Paper>
                        );
                    })}
                </Box>
            )}

            {!selectedBib && visiblePending.length === 0 && (
                <Paper sx={{ p: 3, mb: 3, textAlign: "center", borderRadius: 3 }} elevation={0} variant="outlined">
                    <CheckCircleIcon color="success" sx={{ fontSize: 40, mb: 1 }} />
                    <Typography>Tous les dossards de la liste de départ sont saisis.</Typography>
                </Paper>
            )}

            {done.length > 0 && (
                <>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Déjà confirmés — toucher pour corriger
                    </Typography>
                    <Paper variant="outlined" sx={{ borderRadius: 2 }}>
                        <List dense disablePadding>
                            {done.map((a) => {
                                const e = entriesByBib[String(a.bib)] || {};
                                const s1 = e.style1?.value ?? 0;
                                const s2 = e.style2?.value ?? 0;
                                const j = e.jump?.value ?? 0;
                                return (
                                    <ListItemButton
                                        key={a.bib}
                                        selected={String(selectedBib) === String(a.bib)}
                                        onClick={() => openCorrection(a.bib)}
                                    >
                                        <ListItemText
                                            primary={`[${a.bib}] ${athleteName(a) || "—"}`}
                                            secondary={`J1 ${s1}s · J2 ${s2}s · Total ${(s1 + s2).toFixed(2)}s · Jump ${j}s`}
                                        />
                                    </ListItemButton>
                                );
                            })}
                        </List>
                    </Paper>
                </>
            )}
        </Box>
    );
}